import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import DashboardLayout from '../components/layout/DashboardLayout';
import Container from '../components/layout/Container';
import FormInput from '../components/tickets/form/FormInput';
import OrganizationSection from '../components/organization/OrganizationSection';
import TrustedDevicesSettings from '../components/settings/TrustedDevicesSettings';
import { userService } from '../services/userService';
import { useAuthContext } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { formatProfilePictureUrl } from '../utils/imageUtils';
import {
    User as UserIcon,
    KeyRound,
    Upload,
    AlertCircle,
    CheckCircle
} from 'lucide-react';

type ChangePasswordFormData = {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
};

export default function ProfilePage() {
    const { theme } = useTheme();
    const { t } = useTranslation();
    const { user, updateUser } = useAuthContext();

    // Password change state
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [passwordError, setPasswordError] = useState<string | null>(null);
    const [passwordSuccess, setPasswordSuccess] = useState(false);

    // Profile picture upload state
    const [isUploading, setIsUploading] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [uploadSuccess, setUploadSuccess] = useState<string | null>(null);

    // Define schema inside component where t is available
    const changePasswordSchema = z.object({
        currentPassword: z.string().min(1, t('pages.changePassword.validation.currentRequired')),
        newPassword: z.string()
            .min(6, t('pages.changePassword.validation.minLength'))
            .max(50, t('pages.changePassword.validation.maxLength')),
        confirmPassword: z.string().min(1, t('pages.changePassword.validation.confirmRequired')),
    }).refine((data) => data.newPassword === data.confirmPassword, {
        message: t('pages.changePassword.validation.passwordsDontMatch'),
        path: ["confirmPassword"],
    });

    const { register, handleSubmit, formState: { errors }, reset } = useForm<ChangePasswordFormData>({
        resolver: zodResolver(changePasswordSchema)
    });

    const onPasswordSubmit = async (data: ChangePasswordFormData) => {
        setIsSubmitting(true);
        setPasswordError(null);
        try {
            await userService.changePassword({
                currentPassword: data.currentPassword,
                newPassword: data.newPassword
            });
            setPasswordSuccess(true);
            reset();
            setTimeout(() => {
                setPasswordSuccess(false);
            }, 3000);
        } catch (err) {
            setPasswordError(err instanceof Error ? err.message : t('pages.changePassword.failedToChange'));
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleProfilePictureUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // Validate file type
        if (!file.type.startsWith('image/')) {
            setUploadError(t('profile.selectImageFile', 'Please select an image file'));
            return;
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
            setUploadError(t('profile.imageSizeLimit', 'Image size should be less than 5MB'));
            return;
        }

        setIsUploading(true);
        setUploadError(null);

        try {
            const data = await userService.uploadProfilePicture(file);

            // Update user context with new profile picture URL
            if (updateUser && user) {
                updateUser({
                    ...user,
                    pfp_url: data.pfp_url
                });
            }
            setUploadSuccess(t('profile.uploadSuccess', 'Profile picture uploaded successfully'));
            setTimeout(() => {
                setUploadSuccess(null);
            }, 3000);
        } catch (error) {
            console.error('Error uploading profile picture:', error);
            setUploadError(t('profile.uploadFailed', 'Failed to upload profile picture'));
        } finally {
            setIsUploading(false);
        }
    };

    return (
    <DashboardLayout>
      <Container className="py-8 max-w-6xl">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-3 mb-2">
            <div className={`p-2 rounded-lg ${theme === 'dark' ? 'bg-indigo-900/50' : 'bg-indigo-100'}`}>
              <UserIcon className={`h-6 w-6 ${theme === 'dark' ? 'text-indigo-400' : 'text-indigo-600'}`} />
            </div>
            <h1 className={`text-3xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              {t('pages.profile.title', 'My Profile')}
            </h1>
          </div>
          <p className={`text-sm ml-14 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            {t('pages.profile.description', 'Manage your profile, organization, and security settings')}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Profile Info */}
          <div className="lg:col-span-1 space-y-6">
            {/* Profile Card */}
            <div className={`rounded-xl border shadow-sm overflow-hidden ${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
              {/* Profile Header with Gradient */}
              <div className={`h-24 ${theme === 'dark' ? 'bg-gradient-to-r from-indigo-900 to-purple-900' : 'bg-gradient-to-r from-indigo-500 to-purple-600'}`}></div>
              
              <div className="px-6 pb-6">
                {/* Profile Picture - Overlapping the gradient */}
                <div className="flex flex-col items-center -mt-12">
                  <div className="relative group">
                    <div className={`h-24 w-24 rounded-full overflow-hidden ring-4 ${theme === 'dark' ? 'ring-gray-800' : 'ring-white'} shadow-lg`}>
                      {user?.pfp_url ? (
                        <img 
                          src={formatProfilePictureUrl(user.pfp_url)} 
                          alt={t('common.profile')} 
                          className="h-full w-full object-cover"
                          onError={(e) => {
                            const imgElement = e.target as HTMLImageElement;
                            imgElement.style.display = 'none';
                            const nextElement = imgElement.nextElementSibling as HTMLElement;
                            if (nextElement) {
                              nextElement.style.display = 'flex';
                            }
                          }}
                        />
                      ) : null}
                      <div 
                        className={`${user?.pfp_url ? 'hidden' : 'flex'} h-full w-full bg-gradient-to-br from-indigo-500 to-purple-600 items-center justify-center`}
                      >
                        <span className="text-white font-bold text-3xl">
                          {user?.full_name ? user.full_name.charAt(0).toUpperCase() : '?'}
                        </span>
                      </div>
                    </div>
                    
                    {/* Upload Overlay */}
                    <label 
                      htmlFor="profile-picture-upload"
                      className={`absolute inset-0 flex items-center justify-center rounded-full cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity ${
                        theme === 'dark' ? 'bg-black/60' : 'bg-black/50'
                      }`}
                    >
                      <Upload className="h-6 w-6 text-white" />
                      <input
                        id="profile-picture-upload"
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleProfilePictureUpload}
                        disabled={isUploading}
                      />
                    </label>
                  </div>
                  
                  {/* Upload Status */}
                  {isUploading && (
                    <div className="mt-2 text-xs text-indigo-500">
                      {t('common.loading', 'Uploading...')}
                    </div>
                  )}
                  {uploadError && (
                    <div className="mt-2 flex items-center space-x-1 text-red-500 text-xs">
                      <AlertCircle className="h-3 w-3" />
                      <span>{uploadError}</span>
                    </div>
                  )}
                  {uploadSuccess && (
                    <div className="mt-2 flex items-center space-x-1 text-green-500 text-xs">
                      <CheckCircle className="h-3 w-3" />
                      <span>{uploadSuccess}</span>
                    </div>
                  )}
                </div>

                {/* User Info */}
                <div className="mt-4 text-center space-y-3">
                  <div>
                    <h2 className={`text-xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      {user?.full_name}
                    </h2>
                    <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                      {user?.email}
                    </p>
                  </div>
                  
                  <div className="flex items-center justify-center">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium capitalize ${
                      theme === 'dark'
                        ? 'bg-indigo-900/50 text-indigo-300'
                        : 'bg-indigo-100 text-indigo-800'
                    }`}>
                      {user?.role}
                    </span>
                  </div>
                  
                  {user?.phone && (
                    <div className={`pt-3 border-t ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
                      <p className={`text-xs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>
                        {t('pages.profile.phone', 'Phone')}
                      </p>
                      <p className={`text-sm mt-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                        {user.phone}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Organization & Security */}
          <div className="lg:col-span-2 space-y-6">
            {/* Organization Section */}
            <div className={`rounded-xl border shadow-sm p-6 ${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
              <div className="flex items-center space-x-2 mb-6">
                <div className={`p-2 rounded-lg ${theme === 'dark' ? 'bg-indigo-900/50' : 'bg-indigo-100'}`}>
                  <UserIcon className={`h-5 w-5 ${theme === 'dark' ? 'text-indigo-400' : 'text-indigo-600'}`} />
                </div>
                <h2 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  {t('pages.profile.organization', 'Organization')}
                </h2>
              </div>
              
              <OrganizationSection />
            </div>

            {/* Change Password Section */}
            <div className={`rounded-xl border shadow-sm p-6 ${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
              <div className="flex items-center space-x-2 mb-6">
                <div className={`p-2 rounded-lg ${theme === 'dark' ? 'bg-indigo-900/50' : 'bg-indigo-100'}`}>
                  <KeyRound className={`h-5 w-5 ${theme === 'dark' ? 'text-indigo-400' : 'text-indigo-600'}`} />
                </div>
                <h2 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  {t('pages.changePassword.title', 'Change Password')}
                </h2>
              </div>
              
              {passwordError && (
                <div className={`mb-4 p-4 rounded-lg flex items-start space-x-2 ${
                  theme === 'dark'
                    ? 'bg-red-900/30 border border-red-800/50 text-red-200'
                    : 'bg-red-50 border border-red-200 text-red-800'
                }`}>
                  <AlertCircle className={`h-5 w-5 flex-shrink-0 ${
                    theme === 'dark' ? 'text-red-400' : 'text-red-500'
                  } mt-0.5`} />
                  <span className="text-sm">{passwordError}</span>
                </div>
              )}

              {passwordSuccess && (
                <div className={`mb-4 p-4 rounded-lg flex items-start space-x-2 ${
                  theme === 'dark'
                    ? 'bg-green-900/30 border border-green-800/50 text-green-200'
                    : 'bg-green-50 border border-green-200 text-green-800'
                }`}>
                  <CheckCircle className={`h-5 w-5 flex-shrink-0 ${
                    theme === 'dark' ? 'text-green-400' : 'text-green-500'
                  } mt-0.5`} />
                  <span className="text-sm">{t('pages.changePassword.success')}</span>
                </div>
              )}

              <form onSubmit={handleSubmit(onPasswordSubmit)} className="space-y-4">
                <FormInput
                  label={t('pages.changePassword.currentPassword')}
                  name="currentPassword"
                  type="password"
                  required
                  register={register}
                  error={errors.currentPassword?.message}
                  disabled={isSubmitting}
                />

                <FormInput
                  label={t('pages.changePassword.newPassword')}
                  name="newPassword"
                  type="password"
                  required
                  register={register}
                  error={errors.newPassword?.message}
                  disabled={isSubmitting}
                />

                <FormInput
                  label={t('pages.changePassword.confirmPassword')}
                  name="confirmPassword"
                  type="password"
                  required
                  register={register}
                  error={errors.confirmPassword?.message}
                  disabled={isSubmitting}
                />

                <div className="flex items-center justify-end pt-2">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`inline-flex items-center justify-center rounded-lg px-6 py-2.5 text-sm font-medium text-white shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                      theme === 'dark'
                        ? 'bg-indigo-600 hover:bg-indigo-500 focus:ring-indigo-400 focus:ring-offset-gray-800'
                        : 'bg-indigo-600 hover:bg-indigo-700 focus:ring-indigo-500'
                    } ${isSubmitting ? 'opacity-75 cursor-not-allowed' : ''}`}
                  >
                    {isSubmitting ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        {t('pages.changePassword.changing')}
                      </>
                    ) : (
                      t('pages.changePassword.submit')
                    )}
                  </button>
                </div>
              </form>
            </div>

            {/* Trusted Devices / Two-Factor Security Section */}
            <div className={`rounded-xl border shadow-sm p-6 ${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
              <div className="flex items-center space-x-2 mb-6">
                <div className={`p-2 rounded-lg ${theme === 'dark' ? 'bg-indigo-900/50' : 'bg-indigo-100'}`}>
                  <KeyRound className={`h-5 w-5 ${theme === 'dark' ? 'text-indigo-400' : 'text-indigo-600'}`} />
                </div>
                <h2 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  {t('pages.profile.twoFactorSecurity', 'Two-Factor Security')}
                </h2>
              </div>
              <TrustedDevicesSettings />
            </div>
          </div>
        </div>
      </Container>
    </DashboardLayout>
  );
}
